import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ModalShellComponent, ModalShellConfig } from '@shared/components/modal-shell/modal-shell.component';

export interface PersonAdminDeleteDialogData {
  mode: 'logical' | 'physical';
  fullName: string;
  isBulk?: boolean;
}

export type PersonAdminDeleteDialogResult = { reason: string } | true;

@Component({
  selector: 'app-person-admin-delete-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, ModalShellComponent],
  template: `
    <app-modal-shell [config]="modalConfig">
      <div modal-content>
        <ng-container *ngIf="data.mode === 'logical'; else physicalContent">
          <p *ngIf="data.isBulk; else singleLogical">Se eliminarán lógicamente <strong>{{ data.fullName }}</strong>. El motivo ingresado se aplicará a todos.</p>
          <ng-template #singleLogical><p>Se eliminará lógicamente la ficha de <strong>{{ data.fullName }}</strong>.</p></ng-template>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Motivo de eliminación</mat-label>
            <textarea matInput rows="3" maxlength="500" [formControl]="reasonControl"></textarea>
            <mat-error *ngIf="reasonControl.hasError('required')">Ingresá un motivo.</mat-error>
            <mat-error *ngIf="reasonControl.hasError('minlength')">El motivo debe tener al menos 5 caracteres.</mat-error>
          </mat-form-field>
        </ng-container>
        <ng-template #physicalContent>
          <p *ngIf="data.isBulk; else singlePhysical">Se eliminarán definitivamente <strong>{{ data.fullName }}</strong> y sus datos asociados. Esta acción no se puede deshacer.</p>
          <ng-template #singlePhysical><p>Se eliminará definitivamente la ficha de <strong>{{ data.fullName }}</strong> y sus datos asociados. Esta acción no se puede deshacer.</p></ng-template>
        </ng-template>
      </div>
      <ng-container modal-actions>
        <button mat-button type="button" (click)="dialogRef.close()">Cancelar</button>
        <button mat-flat-button color="warn" type="button" [disabled]="!canConfirm" (click)="confirm()">
          {{ data.mode === 'logical' ? 'Borrar' : 'Eliminar definitivamente' }}
        </button>
      </ng-container>
    </app-modal-shell>
  `,
  styles: [`.full-width { width: 100%; } p { margin-top: 0; }`],
})
export class PersonAdminDeleteDialogComponent {
  readonly modalConfig: ModalShellConfig;
  readonly reasonControl = new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(5), Validators.maxLength(500)] });

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: PersonAdminDeleteDialogData,
    readonly dialogRef: MatDialogRef<PersonAdminDeleteDialogComponent, PersonAdminDeleteDialogResult | null>,
  ) {
    this.modalConfig = {
      title: data.mode === 'logical' ? 'Eliminar miembro' : 'Borrado físico',
      size: 'sm',
      layout: 'confirm',
      tone: 'danger',
    };
  }

  get canConfirm(): boolean {
    return this.data.mode === 'logical'
      ? this.reasonControl.valid
      : true;
  }

  confirm(): void {
    if (!this.canConfirm) return;
    this.dialogRef.close(this.data.mode === 'logical'
      ? { reason: this.reasonControl.value.trim() }
      : true);
  }
}
